import React from 'react';
import { Box } from '@chakra-ui/react';
import { useAppData } from '../store/dataStore';
import {
  FaUsers,
  FaClock,
  FaGraduationCap,
  FaBriefcase,
  FaRocket,
  FaCode,
  FaShieldAlt,
  FaLaptop,
  FaBrain,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  HeroSection,
  PersonaSection,
  CurriculumSection,
  ProjectShowcase,
  LearningExperience,
  SuccessStories,
  InvestmentSection,
  RiskReversal,
  UrgencySection,
  FAQSection,
  FinalCTA,
} from '../components/beginnerTrackComponents';
import { CodeverseMentorSection } from '../components/CodeverseMentorSection';
import { Helmet } from 'react-helmet-async';
import { useAnalytics } from '../hooks/useAnalytics'; // ✅ Added analytics hook
import { useAffiliateCode } from '../hooks/useAffiliateCode';

const BeginnerTrackPage = () => {
  const { appData } = useAppData();
  const { studentsCount, course1 } = appData;
  const { trackEvent } = useAnalytics(); // ✅ Use analytics hook

  // Initialize affiliate code tracking
  useAffiliateCode('beginner_track_visit');

  // ---------------- PAGE DATA ----------------
  const pageData = {
    hero: {
      badge: "Confidence First",
      title: "Go from zero > JobReady GenAI developer",
      description:
        "Learn by building with weekly mentoring, review gates, and your first real deployed app.",

      trustElements: [
        { icon: FaUsers, text: `${studentsCount}+ Enrolled` },
        { icon: FaShieldAlt, text: '15-Day Monday back guarantee' },
      ],
      ctaButton: {
        text: "Check if this program is right for you",
        href: "/assessment",
      },
    },

    personas: {
      title: "Who Should Join?",
      description: "Designed for beginners who want to start with confidence.",
      cards: [
        {
          icon: FaGraduationCap,
          title: "Fresh Graduates",
          desc: "Learn how companies actually build AI products. You will ship your first app.",
          badge: "Primary",
        },
        {
          icon: FaBriefcase,
          title: "Career Switchers",
          desc: "Start from fundamentals with guidance. No coding anxiety.",
        },
        {
          icon: FaRocket,
          title: "Restarters",
          desc: "Rebuild your tech profile with portfolio projects, not theory.",
        },
      ],
    },


    curriculum: {
      title: 'A Practical Path to Confidence',
      description:
        'You learn engineering by building. Every module leads to a working outcome. No endless theory.',
      phases: [
        {
          duration: '4 Weeks',
          title: 'Python & AI Programming Fundamentals',
          desc: 'Master Python with AI-focused projects • Variables to OOP • Error handling • File operations • API integration basics',
          projects: '8 mini Projects + AI Assistant',
        },
        {
          duration: '2 Weeks',
          title: 'Machine Learning & Deep Learning',
          desc: 'Real-world ML with Scikit-learn • Neural Networks • Model evaluation • Bias-variance tradeoffs • Regression & Classification',
          projects: '2 ML Projects',
        },
        {
          duration: '2 Weeks',
          title: 'Natural Language Processing',
          desc: 'Advanced Prompt Engineering • NLP Fundamentals • LLM Architecture • Transformers • Vector Embeddings • Text Processing',
          projects: '2 NLP Applications',
        },
        {
          duration: '4 Weeks',
          title: 'Full-Stack AI Web Development',
          desc: 'Flask & FastAPI REST APIs • NiceGUI for AI interfaces • Build React Apps • Deployable web apps • API development',
          projects: '3 Web Apps + APIs',
        },
        {
          duration: '4 Weeks',
          title: 'LLM Development - Generative AI Mastery',
          desc: 'GPT-5, Claude, LLaMA models • Advanced prompt engineering • LangChain • RAG systems • Vector databases • Real AI products',
          projects: '5 GenAI Apps',
        },
        {
          duration: '2 Weeks',
          title: 'Cloud & Production Deployment',
          desc: 'Docker containerization • AWS/Firebase Cloud deployment • CI/CD pipelines • Monitoring • Performance optimization',
          projects: 'Live Deployment',
        },
        {
          duration: '2 Weeks',
          title: 'Databases & AI Development Tools',
          desc: 'RDBMS & NoSQL databases • Git & GitHub • AI-assisted development • Code generation tools • Development workflows',
          projects: 'Database Apps',
        },
        {
          duration: '2 Weeks',
          title: 'Capstone Project & Career Launch',
          desc: 'End-to-end project development • Real-world AI solution • Portfolio presentation • Interview preparation • Career strategy',
          projects: '1 Major Capstone',
        },
      ],
    },

    projects: {
      title: "10+ Projects You'll Build",
      description: 'Portfolio-ready projects with GitHub repos and live demos.',
      list: [
        'Enterprise RAG Chatbot with Vector DB',
        'Multi-format Document Summarizer',
        'Real-time Market Sentiment Analyzer',
        'AI-Powered Image Caption Generator',
        'Intelligent Code Assistant & Debugger',
        'Smart Resume Parser & ATS Optimizer',
        'LLM-Powered Customer Support System',
        'Generative AI Content Creator',
        'Data Analysis & Visualization Assistant',
        'API-First AI Microservices Platform',
        'Multi-Model AI Comparison Dashboard',
        'Production-Ready ChatGPT Clone',
      ],
    },

    learning: {
      title: 'Master Faster with Our Structured Journey',
      description:
        'Spend less time guessing, more time building. Our proven weekly rhythm combines live guidance with hands-on practice for maximum results.',
      schedule: [
        { icon: FaClock, day: '3-days in a week', activity: '3 Live sessions + demonstrations', type: 'live' },
        { icon: FaCode, day: '2-days in a week', activity: 'Coding practice + 5 online tests', type: 'practice' },
        { icon: FaLaptop, day: '1-Day', activity: 'Project work + assignment submission', type: 'practice' },
        { icon: FaBrain, day: 'Saturday/Sunday', activity: 'Live support session + Q&A', type: 'live' },
      ],
      expectations: {
        title: 'Your Weekly Success Package',
        items: [
          '3 live interactive classes',
          '1 dedicated support session',
          '5 comprehensive online tests',
          'Good amount of self-paced practice',
          'Assignment submission with feedback',
          '24/7 community support access',
        ],
      },
    },

    success: {
      title: 'Success Stories',
      description: 'Real transformations from complete beginners to AI professionals.',
      stories: [
        { name: 'Priya S.', before: 'B.Tech ECE', after: 'ML Engineer @ Tech Startup' },
        { name: 'Rahul M.', before: 'B. Tech CS', after: 'AI Developer @ Tech Startup' },
        { name: 'Yashwanth', before: 'B. Tech CS', after: 'AI Developer @ FinTech' },
      ],
    },

    investment: {
      leftSide: {
        title: 'Invest in Your Future',
        description: 'Complete program with lifetime access and career support.',
        features: [
          '24 weeks intensive training',
          '10+ portfolio projects',
          'Lifetime community access',
          'Career guidance & mentorship',
          'Certificate of completion',
        ],
        comparison: 'Compare: Traditional bootcamps ₹2-3L | Our program: Much more affordable',
      },
      pricing: {
        badge: 'Limited Time Offer',
        mrp: course1.mrp,
        price: course1.price,
        discountText: 'Special early bird discount - Save big!',
        emi: course1.emi,
        guarantee: '14-Day Money Back Guarantee',
        ctaHref: '/enroll?courseId=1',
        included: [
          '24 weeks intensive training',
          '10+ real-world projects',
          'Lifetime community access',
          '1:1 career mentorship',
          'Certificate of completion',
          'Course recordings & materials',
          'Weekly code reviews',
          'Interview preparation',
        ],
      },
    },

    riskReversal: {
      icon: FaShieldAlt,
      title: '15-Day Full Access Trial',
      description:
        'Attend real live sessions and start your first AI use case. If you don’t like it, get a full refund. No questions.',
      stats: [
        { value: '15 Days', label: 'Try the actual course' },
        { value: '100%', label: 'Refund' },
        { value: '0', label: 'Risk' },
      ],
    },

    urgency: {
      badge: `Next Batch Starts: ${course1.startDate}`,
      title: 'Limited seats are available',
    },

    faq: {
      title: 'Frequently Asked Questions',
      description: 'Everything you need to know about the Beginner Track.',
      items: [
        {
          q: 'I have zero coding experience—can I do this?',
          a: 'Absolutely! This track is designed for complete beginners, but the basic understanding of programming fundamentals is important.',
        },
        {
          q: 'What if I fall behind in the course?',
          a: 'Access to study materials up to 12 months. You will be provided with personalized support. Our motto is NPLB - No person left behind',
        },
        {
          q: 'Do I need a powerful computer?',
          a: 'Basic laptop with 4GB RAM is sufficient for most projects.',
        },
        {
          q: 'Will I get job placement assistance?',
          a: 'Yes, career guidance, resume review, and interview prep included. You also get the support for finding the job opportunities',
        },
      ],
    },

    finalCTA: {
      title: 'Start your confidence journey',
      description: `Join ${studentsCount}+ learners who built real GenAI apps in their first 50 days.`,
      buttons: [
        { text: 'Check If This Program Is Right For Me', href: '/assessment', variant: 'solid' },
        { text: 'Talk to Advisor', href: '/contact', variant: 'outline' },
      ],
      footer: '✓ Full access trial ✓ No coding anxiety ✓ Build a deployed AI app',
    },

    codeverseMentor: {
      badge: 'Exclusive Learning Tool',
      title: 'Learn Smarter with Codeverse Mentor',
      subtitle: 'ChatGPT gives answers. Codeverse Mentor builds developers.',
      comparisonTable: {
        codeverseColumn: 'Codeverse Mentor',
        otherToolsColumn: 'ChatGPT & Other AI',
        rows: [
          { feature: 'Proactive step-by-step guidance' },
          { feature: 'Real-time code validation' },
          { feature: 'Context-aware assistance' },
          { feature: 'Zero prompt anxiety' },
          { feature: 'Progress tracking with milestones' },
          { feature: 'Builds independence through micro-steps' },
        ],
      },
      featuresTitle: 'How Codeverse Mentor Transforms Your Learning',
      features: [
        {
          title: 'Structured Project Journey',
          description: 'Curated projects broken into achievable micro-steps with clear objectives and validation criteria. Progress automatically saved between sessions.',
        },
        {
          title: 'Real-Time Validation',
          description: 'Instant feedback on your code directly in VS Code. Detects common mistakes and suggests fixes. Unlocks next steps only when you\'ve mastered current concepts.',
        },
        {
          title: 'Context-Aware AI Assistance',
          description: 'Get help tailored to your specific step and code. Pre-structured prompts eliminate guesswork and help you learn how to ask better questions over time.',
        },
        {
          title: 'Guided Learning Paths',
          description: 'Curated projects that build skills progressively, ensuring you master fundamentals before moving to advanced concepts.',
        },
        {
          title: 'Smart Hints System',
          description: 'Get unstuck without getting the answer handed to you. Learn to think like a developer and solve problems independently.',
        },
        {
          title: 'Gamified Learning',
          description: 'Earn points and badges for completing challenges. Track your progress visually with achievement milestones.',
        },
      ],
      freeAccess: {
        badge: 'Included Free',
        title: 'Free Access for Entire Course Duration',
        description: 'Work on projects with ease using our proprietary VS Code extension. Available exclusively to all enrolled students at no additional cost.',
        buttonText: 'Learn More About Codeverse Mentor',
        link: 'https://marketplace.visualstudio.com/items?itemName=OjasaMirai.codeverse-mentor',
      },
    },

  };

  // ---------------- EVENT HANDLERS ----------------
  const handleEnrollClick = (section) => {
    // ✅ Analytics for Early Bird CTA
    if (section === 'pricing') {
      trackEvent('pricing_cta_click', {
        page: 'beginner_track',
        plan: 'beginner_24_week',
        price: '59994',
        discount: 'early_bird',
      });
    }
    console.log(`Enroll clicked from ${section}`);
  };

  const handleTalkToAdvisorClick = () => {
    // ✅ Analytics for Talk to Advisor button
    trackEvent('talk_to_advisor_click', {
      page: 'beginner_track',
      section: 'final_cta',
      cta_text: 'Talk to Advisor',
    });
    console.log('Talk to Advisor clicked');
  };

  const handleVideoPlay = () => {
    console.log('Video play clicked');
  };

  // ---------------- PAGE RENDER ----------------
  return (
    <>
      <Helmet>
        <title>Beginner GenAI Track | Zero to AI Developer in 24 Weeks</title>
        <meta
          name="description"
          content="Master Python, Machine Learning, and Generative AI from scratch. Join the 24-week Beginner Track designed for students and career switchers to become job-ready GenAI developers."
        />
      </Helmet>

      <Box>
        <Navbar />

        <HeroSection
          data={pageData.hero}
          onEnrollClick={() => handleEnrollClick('hero')}
          onVideoPlay={handleVideoPlay}
        />

        <PersonaSection data={pageData.personas} />
        <CurriculumSection data={pageData.curriculum} />
        <ProjectShowcase data={pageData.projects} />
        <LearningExperience data={pageData.learning} />
        <CodeverseMentorSection data={pageData.codeverseMentor} />
        <SuccessStories data={pageData.success} />

        <InvestmentSection
          data={pageData.investment}
          onEnrollClick={() => handleEnrollClick('pricing')}
        />

        <RiskReversal data={pageData.riskReversal} />
        <UrgencySection data={pageData.urgency} />
        <FAQSection data={pageData.faq} />

        <FinalCTA
          data={pageData.finalCTA}
          onEnrollClick={() => handleEnrollClick('final')}
          onTalkToAdvisorClick={handleTalkToAdvisorClick}
        />

        <Footer />
      </Box>
    </>
  );
};

export default BeginnerTrackPage;
